/**
 * CSV import contract (M6). Two entity targets cover the full-week-test onboarding need:
 * `customers` (with an optional service address → property and a primary contact) and
 * `price_book`. The client sends the raw CSV text + a column mapping (target field → source
 * header); the SERVER re-parses and validates (never trusts client-parsed rows). Idempotent:
 * the whole commit carries (clientId, mutationId), and each row dedupes on a natural key.
 */
import { z } from 'zod';
import { idempotency } from './spine';

export const IMPORT_ENTITIES = ['customers', 'price_book'] as const;
export type ImportEntity = (typeof IMPORT_ENTITIES)[number];

export interface ImportFieldSpec {
  key: string;
  label: string;
  required: boolean;
  hint?: string;
}

/** The mappable target fields per entity — drives the web mapping UI and server validation. */
export const IMPORT_FIELDS: Record<ImportEntity, ImportFieldSpec[]> = {
  customers: [
    { key: 'displayName', label: 'Customer name', required: true },
    { key: 'kind', label: 'Type (residential/commercial)', required: false },
    { key: 'companyName', label: 'Company', required: false },
    { key: 'email', label: 'Email', required: false },
    { key: 'phone', label: 'Phone', required: false },
    { key: 'city', label: 'City', required: false },
    { key: 'state', label: 'State (2-letter)', required: false },
    {
      key: 'address1',
      label: 'Service address',
      required: false,
      hint: 'creates a property',
    },
    { key: 'propertyLabel', label: 'Property label', required: false },
    { key: 'contactFirstName', label: 'Contact first name', required: false },
    { key: 'contactLastName', label: 'Contact last name', required: false },
    { key: 'contactEmail', label: 'Contact email', required: false },
    { key: 'contactPhone', label: 'Contact phone', required: false },
    {
      key: 'leadSource',
      label: 'How they found you',
      required: false,
      hint: 'referral, Google, repeat…',
    },
  ],
  price_book: [
    { key: 'name', label: 'Item name', required: true },
    { key: 'kind', label: 'Kind (service/labor/material/fee)', required: false },
    {
      key: 'unitPrice',
      label: 'Unit price ($)',
      required: false,
      hint: 'required for new items; retained for existing items',
    },
    { key: 'sku', label: 'SKU', required: false },
    {
      key: 'unitCost',
      label: 'Unit cost ($)',
      required: false,
      hint: 'opening stock only, not sell price',
    },
    { key: 'openingQty', label: 'Opening quantity', required: false },
    {
      key: 'stockLocation',
      label: 'Stock location',
      required: false,
      hint: 'location ID or exact name; quantity and cost required',
    },
    { key: 'description', label: 'Description', required: false },
    { key: 'unitLabel', label: 'Unit', required: false },
    { key: 'taxable', label: 'Taxable (yes/no)', required: false },
    { key: 'category', label: 'Category', required: false },
  ],
};

/** A column mapping: target field key → source CSV header (only mapped fields are read). */
export const importMapping = z.record(z.string(), z.string());
export type ImportMapping = z.infer<typeof importMapping>;

const base = z.object({
  entity: z.enum(IMPORT_ENTITIES),
  csv: z.string().min(1).max(5_000_000), // ~5 MB guard
  mapping: importMapping,
});

export const importPreviewInput = base;
export type ImportPreviewInput = z.infer<typeof importPreviewInput>;

export const importCommitInput = idempotency.extend(base.shape);
export type ImportCommitInput = z.infer<typeof importCommitInput>;
